import React, { useState } from 'react';
import { Auth, Hub } from 'aws-amplify';
import {
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';

function OtpLogin({ onBack }) {
  const [step, setStep] = useState('email'); // email | emailCode | smsCode
  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [userSession, setUserSession] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const resetState = () => {
    setStep('email');
    setEmailCode('');
    setSmsCode('');
    setUserSession(null);
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const user = await Auth.signIn(email.trim());

      if (user.challengeName === 'CUSTOM_CHALLENGE') {
        setUserSession(user);
        setStep('emailCode');
        setSuccessMessage('A verification code has been sent to your email.');
      } else {
        setErrorMessage('Unexpected authentication response.');
      }
    } catch (error) {
      console.error('OTP request error:', error);

      if (error.code === 'UserNotFoundException') {
        setErrorMessage('No account found with that email.');
      } else if (error.code === 'NotAuthorizedException') {
        setErrorMessage('This account is not enabled for one-time login.');
      } else {
        setErrorMessage(error.message || 'Failed to send one-time code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmailCode = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const result = await Auth.sendCustomChallengeAnswer(userSession, emailCode.trim());

      if (result.challengeName === 'CUSTOM_CHALLENGE') {
        setUserSession(result);
        setStep('smsCode');
        setSuccessMessage('A verification code has been sent to your phone.');
      } else {
        Hub.dispatch('auth', { event: 'signIn' });
      }
    } catch (error) {
      console.error('Email OTP verify error:', error);
      resetState();
      setErrorMessage('Invalid email code. Please request a new code and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySmsCode = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const result = await Auth.sendCustomChallengeAnswer(userSession, smsCode.trim());

      if (!result.challengeName) {
        Hub.dispatch('auth', { event: 'signIn' });
      } else {
        setErrorMessage('Unexpected authentication response. Please try again.');
      }
    } catch (error) {
      console.error('SMS OTP verify error:', error);
      resetState();
      setErrorMessage('Invalid SMS code. Please request a new code and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', width: '280px' }}>
      <Typography variant="h6" style={{ textAlign: 'center' }}>
        One-Time Password Login
      </Typography>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      {successMessage && <Alert severity="success">{successMessage}</Alert>}

      {step === 'email' && (
        <form
          onSubmit={handleRequestOtp}
          style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}
        >
          <Typography variant="body2" style={{ textAlign: 'center' }}>
            Enter your email to receive a one-time login code.
          </Typography>

          <TextField
            label="Email"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
          />

          <Button
            type="submit"
            color="primary"
            variant="contained"
            size="large"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Send One-Time Code'}
          </Button>

          <Typography variant="caption" style={{ textAlign: 'center', color: '#888' }}>
            First we will send a code to your email, then a second code to your phone.
          </Typography>

          <Button onClick={onBack} color="inherit" size="small">
            Back to Login
          </Button>
        </form>
      )}

      {step === 'emailCode' && (
        <form
          onSubmit={handleVerifyEmailCode}
          style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}
        >
          <Typography variant="body2" style={{ textAlign: 'center' }}>
            Enter the code sent to your registered email.
          </Typography>

          <TextField
            label="Email Code"
            variant="outlined"
            value={emailCode}
            onChange={(e) => setEmailCode(e.target.value)}
            required
            disabled={loading}
            autoFocus
          />

          <Button
            type="submit"
            color="primary"
            variant="contained"
            size="large"
            disabled={loading || !emailCode}
          >
            {loading ? <CircularProgress size={24} /> : 'Verify Email Code'}
          </Button>

          <Button
            onClick={() => {
              resetState();
              setErrorMessage('');
              setSuccessMessage('');
            }}
            color="inherit"
            size="small"
          >
            Resend Code
          </Button>

          <Button onClick={onBack} color="inherit" size="small">
            Back to Login
          </Button>
        </form>
      )}

      {step === 'smsCode' && (
        <form
          onSubmit={handleVerifySmsCode}
          style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}
        >
          <Typography variant="body2" style={{ textAlign: 'center' }}>
            Enter the verification code sent to your phone.
          </Typography>

          <TextField
            label="SMS Code"
            variant="outlined"
            value={smsCode}
            onChange={(e) => setSmsCode(e.target.value)}
            required
            disabled={loading}
            autoFocus
          />

          <Button
            type="submit"
            color="primary"
            variant="contained"
            size="large"
            disabled={loading || !smsCode}
          >
            {loading ? <CircularProgress size={24} /> : 'Verify SMS Code'}
          </Button>

          <Button
            onClick={() => {
              resetState();
              setErrorMessage('');
              setSuccessMessage('');
            }}
            color="inherit"
            size="small"
          >
            Resend Code
          </Button>
          <Button onClick={onBack} color="inherit" size="small">
            Back to Login
          </Button>
        </form>
      )}
    </div>
  );
}

export default OtpLogin;
